import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchLeads, fetchSettings, updateSettings } from "@/lib/api";
import { BYMAT_LOGO } from "@/lib/brand";
import { RefreshCw, ArrowLeft, Users, Mail, Phone, Calendar, Download, Save, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

// Convert ISO UTC string -> value usable by <input type="datetime-local">
function isoToLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
}

export default function AdminPage() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [closingInput, setClosingInput] = useState("");
  const [savingDate, setSavingDate] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [leadsData, settings] = await Promise.all([fetchLeads(), fetchSettings()]);
      setLeads(leadsData);
      setClosingInput(isoToLocalInput(settings?.batch_closing_date));
    } catch (e) {
      console.error(e);
      setError("Unable to load leads. Check backend connection.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleSaveDate = async () => {
    if (!closingInput) {
      toast.error("Please pick a date and time");
      return;
    }
    const d = new Date(closingInput);
    if (Number.isNaN(d.getTime())) {
      toast.error("Invalid date");
      return;
    }
    setSavingDate(true);
    try {
      await updateSettings({ batch_closing_date: d.toISOString() });
      toast.success("Batch closing date updated");
    } catch (err) {
      console.error(err);
      toast.error("Failed to update closing date");
    } finally {
      setSavingDate(false);
    }
  };

  const exportCsv = () => {
    const rows = [
      ["Name", "Email", "Phone", "Plan / Fees", "Created At"],
      ...leads.map((l) => [l.name, l.email, l.phone, l.fees, l.created_at]),
    ];
    const csv = rows.map((r) => r.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `bymat-leads-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-[#0A1628] text-white" data-testid="admin-page">
      <header className="border-b border-slate-800 bg-[#0A1628]/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="admin-brand">
            <img src={BYMAT_LOGO} alt="BYMAT" className="h-16 w-auto mix-blend-screen" />
            <span className="font-display font-bold text-white text-sm">
              Admin <span className="text-[#38BDF8]">Console</span>
            </span>
          </Link>
          <Link
            to="/"
            className="text-sm text-slate-400 hover:text-white flex items-center gap-2"
            data-testid="admin-back-link"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Landing
          </Link>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        <div className="flex items-start md:items-center flex-col md:flex-row md:justify-between gap-4 mb-8">
          <div>
            <div className="text-xs font-semibold tracking-[0.2em] uppercase text-[#38BDF8]">
              Control Room
            </div>
            <h1 className="font-display text-3xl md:text-4xl font-bold text-white mt-2">
              Workshop Leads
            </h1>
            <p className="text-slate-400 text-sm mt-2">
              Everyone who submitted the registration form, sorted by newest first.
            </p>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={load}
              className="border-slate-800 hover:border-slate-600 text-white bg-slate-900/40"
              data-testid="admin-refresh-button"
            >
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
            <Button
              onClick={exportCsv}
              disabled={!leads.length}
              className="btn-yellow font-semibold"
              data-testid="admin-export-button"
            >
              <Download className="w-4 h-4 mr-2" /> Export CSV
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10">
          {[
            { icon: Users, label: "Total Leads", val: leads.length },
            {
              icon: Calendar,
              label: "Last 24h",
              val: leads.filter(
                (l) =>
                  new Date(l.created_at).getTime() > Date.now() - 1000 * 60 * 60 * 24
              ).length,
            },
            {
              icon: Mail,
              label: "Paid Plan Interest",
              val: leads.filter((l) => !String(l.fees).toLowerCase().includes("free")).length,
            },
          ].map((s, i) => (
            <div
              key={i}
              className="bg-slate-900/60 border border-slate-800 rounded-2xl p-6"
              data-testid={`admin-stat-${i}`}
            >
              <s.icon className="w-5 h-5 text-[#38BDF8]" />
              <div className="font-display text-3xl font-bold mt-3">{s.val}</div>
              <div className="text-xs text-slate-400 uppercase tracking-widest mt-1">
                {s.label}
              </div>
            </div>
          ))}
        </div>

        {/* Batch closing date control */}
        <div
          className="bg-slate-900/60 border border-slate-800 rounded-2xl p-6 md:p-8 mb-10"
          data-testid="batch-settings-panel"
        >
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-5">
            <div>
              <div className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.2em] uppercase text-[#38BDF8]">
                <Clock className="w-3.5 h-3.5" />
                Batch Settings
              </div>
              <h2 className="font-display text-2xl md:text-3xl font-bold text-white mt-2">
                Batch Closing Date
              </h2>
              <p className="text-slate-400 text-sm mt-2 max-w-lg">
                Set the date and time when the current batch closes. The countdown
                timer on the landing page updates instantly for all visitors.
              </p>
            </div>
          </div>

          <div className="mt-6 flex flex-col sm:flex-row gap-3 sm:items-end">
            <div className="flex-1 space-y-2">
              <Label
                htmlFor="closing-input"
                className="text-slate-300 text-xs uppercase tracking-widest"
              >
                Closing Date & Time (your local timezone)
              </Label>
              <Input
                id="closing-input"
                type="datetime-local"
                value={closingInput}
                onChange={(e) => setClosingInput(e.target.value)}
                className="bg-[#0A1628] border-slate-800 h-12 text-white focus:border-[#38BDF8] focus:ring-[#38BDF8]/30"
                data-testid="closing-date-input"
              />
            </div>
            <Button
              onClick={handleSaveDate}
              disabled={savingDate}
              className="btn-yellow h-12 px-6 font-semibold"
              data-testid="save-closing-date-button"
            >
              <Save className="w-4 h-4 mr-2" />
              {savingDate ? "Saving…" : "Save Closing Date"}
            </Button>
          </div>

          {closingInput && (
            <p className="text-xs text-slate-500 mt-3" data-testid="closing-date-preview">
              Will display as:{" "}
              <span className="text-slate-300">
                {new Date(closingInput).toLocaleString("en-IN", {
                  weekday: "long",
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                  hour: "numeric",
                  minute: "2-digit",
                  hour12: true,
                })}
              </span>
            </p>
          )}
        </div>

        {/* Table */}
        <div className="bg-slate-900/40 border border-slate-800 rounded-2xl overflow-hidden">
          {loading ? (
            <div className="p-10 text-center text-slate-400" data-testid="admin-loading">
              Loading leads…
            </div>
          ) : error ? (
            <div className="p-10 text-center text-red-400" data-testid="admin-error">{error}</div>
          ) : leads.length === 0 ? (
            <div className="p-10 text-center text-slate-500" data-testid="admin-empty">
              No leads yet. Share the landing page to start collecting.
            </div>
          ) : (
            <Table data-testid="admin-leads-table">
              <TableHeader>
                <TableRow className="border-slate-800 hover:bg-transparent">
                  <TableHead className="text-slate-400 uppercase text-[10px] tracking-widest">Name</TableHead>
                  <TableHead className="text-slate-400 uppercase text-[10px] tracking-widest">Email</TableHead>
                  <TableHead className="text-slate-400 uppercase text-[10px] tracking-widest">Phone</TableHead>
                  <TableHead className="text-slate-400 uppercase text-[10px] tracking-widest">Plan / Fees</TableHead>
                  <TableHead className="text-slate-400 uppercase text-[10px] tracking-widest">Submitted</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {leads.map((l) => (
                  <TableRow
                    key={l.id}
                    className="border-slate-800 hover:bg-slate-900/50"
                    data-testid={`admin-row-${l.id}`}
                  >
                    <TableCell className="font-medium text-white">{l.name}</TableCell>
                    <TableCell className="text-slate-300">{l.email}</TableCell>
                    <TableCell className="text-slate-300 flex items-center gap-2">
                      <Phone className="w-3 h-3 text-[#38BDF8]" />
                      {l.phone}
                    </TableCell>
                    <TableCell>
                      <span className="px-2 py-1 rounded-full bg-[#38BDF8]/10 text-[#38BDF8] text-xs font-medium">
                        {l.fees}
                      </span>
                    </TableCell>
                    <TableCell className="text-slate-400 text-xs">
                      {new Date(l.created_at).toLocaleString("en-IN")}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </main>
    </div>
  );
}
